import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";

import {
  adminLogin,
  getAdminOverview,
  getClubDemand,
  setClubSponsored,
  setPhotoStatus,
  setReviewStatus,
  type AdminOverview,
  type ClubDemandRow,
} from "../lib/api/lidoo.functions";
import { InsightsPanel } from "../components/lidoo/insights";

export const Route = createFileRoute("/admin")({
  head: () => ({ meta: [{ title: "Admin | sunbedly" }] }),
  component: AdminPage,
});

function eur(cents: number): string {
  return `EUR ${(cents / 100).toFixed(2)}`;
}

function LoginGate({ onSuccess }: { onSuccess: (password: string) => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(false);
    try {
      const res = await adminLogin({ data: { password } });
      if (res.ok) {
        window.sessionStorage.setItem("sunbedly_admin_pw", password);
        onSuccess(password);
      } else {
        // Say plainly that the door is shut rather than repeating "wrong
        // password" while the login silently refuses everything.
        setBlocked(res.blocked);
        setError(true);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-[#F2EFE9] px-5">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-xl border border-[#1C2430]/10 bg-white/60 p-6"
      >
        <span className="li-display text-2xl font-black tracking-tighter text-[#1C2430]">
          sunbedly<span className="text-[#2C4FD6]">.</span>
        </span>
        <p className="li-mono mt-1 text-[11px] uppercase tracking-widest text-[#5B6472]">Admin</p>
        <input
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="li-display mt-5 h-11 w-full rounded-lg border border-[#1C2430]/15 bg-white/70 px-3 text-sm text-[#1C2430] placeholder:text-[#5B6472] focus:border-[#2C4FD6] focus:outline-none"
        />
        {error ? (
          <p className="mt-2 text-sm text-[#B03030]">
            {blocked ? "Too many attempts. Locked for 15 minutes." : "Wrong password."}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={busy}
          className="li-display mt-4 h-11 w-full rounded-lg bg-[#1C2430] text-sm font-bold text-[#F2EFE9] disabled:opacity-60"
        >
          {busy ? "Checking..." : "Enter"}
        </button>
      </form>
    </div>
  );
}

const th = "li-mono px-3 py-2 text-left text-[10px] uppercase tracking-wider text-[#5B6472]";
const td = "px-3 py-2 text-sm text-[#1C2430] align-top [font-variant-numeric:tabular-nums]";

// Which clubs travellers actually ask about, most first.
//
// This is the outreach list. A club sitting on eleven unanswered requests is a
// different conversation from a cold one, and the number is the pitch: "eleven
// people asked us about you" lands where "you are listed with us" does not.
//
// Loaded on demand rather than with the rest of the dashboard, because it is a
// grouping query over every request ever made and the page should not wait for
// it before showing anything.
function ClubDemand({ password }: { password: string }) {
  const [rows, setRows] = useState<ClubDemandRow[] | null>(null);
  const [days, setDays] = useState<number | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const load = async (window?: number) => {
    setBusy(true);
    setDays(window);
    try {
      const res = await getClubDemand({ data: { password, days: window, limit: 200 } });
      setRows(res.ok ? res.rows : []);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void load(undefined);
    // The password never changes within a session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ranges: [string, number | undefined][] = [
    ["7 days", 7],
    ["30 days", 30],
    ["This season", 180],
    ["All time", undefined],
  ];

  return (
    <section className="mt-10">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="li-display text-lg font-bold tracking-tight text-[#1C2430]">
          Demand by club{rows ? ` (${rows.length})` : ""}
        </h2>
        <div className="flex flex-wrap gap-2">
          {ranges.map(([label, value]) => (
            <button
              key={label}
              type="button"
              onClick={() => void load(value)}
              className={`li-mono rounded-md border px-3 py-1.5 text-[11px] transition-colors ${
                days === value
                  ? "border-[#2C4FD6] bg-[#2C4FD6] text-[#F2EFE9]"
                  : "border-[#1C2430]/20 text-[#1C2430] hover:border-[#2C4FD6]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3 overflow-x-auto rounded-lg border border-[#1C2430]/10">
        <table className="w-full min-w-[880px] border-collapse">
          <thead className="bg-[#E7E2D6]">
            <tr>
              <th className={th}>#</th>
              <th className={th}>Club</th>
              <th className={th}>Where</th>
              <th className={th}>Requests</th>
              <th className={th}>Beds</th>
              <th className={th}>Languages</th>
              <th className={th}>Channel</th>
              <th className={th}>Last</th>
              <th className={th}>Reachable</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1C2430]/10">
            {rows?.map((r, i) => (
              <tr key={r.club_slug}>
                <td className={td}>{i + 1}</td>
                <td className={td}>
                  <a
                    href={`/club/${r.club_slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#2C4FD6] underline underline-offset-2"
                  >
                    {r.club_name}
                  </a>
                  {r.claimed ? (
                    <span className="li-mono ml-2 text-[10px] text-[#1F7A6B]">claimed</span>
                  ) : null}
                </td>
                <td className={td}>
                  {r.town}, {r.region_label}
                </td>
                <td className={`${td} font-bold`}>{r.requests}</td>
                <td className={td}>{r.beds}</td>
                <td className={td}>{r.by_locale}</td>
                <td className={td}>
                  {r.whatsapp ? `wa ${r.whatsapp}` : ""}
                  {r.whatsapp && r.email ? " / " : ""}
                  {r.email ? `mail ${r.email}` : ""}
                  {!r.whatsapp && !r.email ? "-" : ""}
                </td>
                <td className={td}>{r.last_at?.slice(0, 10)}</td>
                <td className={td}>
                  {/* The two things that decide whether this lead is
                      actionable today: can we mail them, and have they
                      already claimed the listing. */}
                  {r.has_email ? "email" : "no email"}
                </td>
              </tr>
            ))}
            {rows && rows.length === 0 ? (
              <tr>
                <td className={td} colSpan={9}>
                  {busy ? "Loading..." : "No requests in this window yet."}
                </td>
              </tr>
            ) : null}
            {!rows ? (
              <tr>
                <td className={td} colSpan={9}>
                  Loading...
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-[#1C2430]/10 bg-white/50 p-4">
      <p className="li-mono text-[10px] uppercase tracking-widest text-[#5B6472]">{label}</p>
      <p className="li-display mt-1 text-2xl font-bold tracking-tight text-[#1C2430]">{value}</p>
    </div>
  );
}

function Dashboard({ password }: { password: string }) {
  const [data, setData] = useState<AdminOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const [busyReviewId, setBusyReviewId] = useState<number | null>(null);
  const [busyPhotoId, setBusyPhotoId] = useState<number | null>(null);
  const [clubQuery, setClubQuery] = useState("");

  const load = () => {
    setLoading(true);
    getAdminOverview({ data: { password } })
      .then((res) => setData(res.ok ? res : null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSponsor = async (slug: string, current: boolean) => {
    setBusySlug(slug);
    await setClubSponsored({ data: { password, slug, sponsored: !current } });
    setData((prev) =>
      prev
        ? {
            ...prev,
            clubs: prev.clubs.map((c) =>
              c.slug === slug ? { ...c, sponsored: current ? 0 : 1 } : c,
            ),
          }
        : prev,
    );
    setBusySlug(null);
  };

  const toggleReview = async (id: number, current: string) => {
    setBusyReviewId(id);
    const next = current === "published" ? "hidden" : "published";
    await setReviewStatus({ data: { password, id, status: next } });
    setData((prev) =>
      prev
        ? { ...prev, reviews: prev.reviews.map((r) => (r.id === id ? { ...r, status: next } : r)) }
        : prev,
    );
    setBusyReviewId(null);
  };

  const actOnPhoto = async (id: number, action: "approve" | "reject") => {
    setBusyPhotoId(id);
    await setPhotoStatus({ data: { password, id, action } });
    setData((prev) =>
      prev
        ? {
            ...prev,
            photos:
              action === "reject"
                ? prev.photos.filter((p) => p.id !== id)
                : prev.photos.map((p) => (p.id === id ? { ...p, status: "approved" } : p)),
          }
        : prev,
    );
    setBusyPhotoId(null);
  };

  if (loading && !data) {
    return <p className="li-mono p-8 text-sm text-[#5B6472]">Loading...</p>;
  }
  if (!data) {
    return <p className="li-mono p-8 text-sm text-[#B03030]">Session expired, reload the page.</p>;
  }

  return (
    <div className="mx-auto max-w-6xl px-5 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="li-display text-2xl font-black tracking-tighter text-[#1C2430]">
            sunbedly<span className="text-[#2C4FD6]">.</span> admin
          </span>
          <p className="li-mono mt-1 text-[11px] text-[#5B6472]">
            Everything a traveler asked for or a club applied for, in one place.
          </p>
        </div>
        <button
          onClick={load}
          className="li-mono rounded-md border border-[#1C2430]/20 px-3 py-1.5 text-[11px] text-[#1C2430] hover:border-[#2C4FD6]"
        >
          Refresh
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-6">
        <StatCard label="Availability requests" value={String(data.totals.requests)} />
        <StatCard label="Spot reservations" value={String(data.totals.reservationGroups)} />
        <StatCard label="Partner applications" value={String(data.totals.applications)} />
        <StatCard label="Reviews" value={String(data.totals.reviews)} />
        <StatCard label="Est. fee this month" value={eur(data.totals.estimatedFeeCentsThisMonth)} />
        <StatCard label="Est. fee all time" value={eur(data.totals.estimatedFeeCentsTotal)} />
      </div>
      <p className="li-mono mt-2 text-[10px] leading-relaxed text-[#5B6472]">
        Estimated fee = what an 8% platform fee would be worth on booked spot reservations. Nothing
        is charged automatically yet, travelers pay the club directly. Use this to decide who to
        call first, or to invoice a club manually for a featured/sponsored placement.
      </p>

      <section className="mt-10">
        <h2 className="li-display text-lg font-bold tracking-tight text-[#1C2430]">
          Spot reservations ({data.reservations.length})
        </h2>
        <div className="mt-3 overflow-x-auto rounded-lg border border-[#1C2430]/10">
          <table className="w-full min-w-[720px] border-collapse">
            <thead className="bg-[#E7E2D6]">
              <tr>
                <th className={th}>When</th>
                <th className={th}>Club</th>
                <th className={th}>Date</th>
                <th className={th}>Spots</th>
                <th className={th}>Guest</th>
                <th className={th}>Contact</th>
                <th className={th}>Party</th>
                <th className={th}>Est. fee</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1C2430]/10">
              {data.reservations.map((r) => (
                <tr key={r.group_code}>
                  <td className={td}>{r.created_at}</td>
                  <td className={td}>{r.club_name ?? r.club_slug}</td>
                  <td className={td}>{r.visit_date}</td>
                  <td className={`${td} li-mono`}>{r.spots}</td>
                  <td className={td}>{r.name}</td>
                  <td className={td}>{r.contact}</td>
                  <td className={td}>{r.party_size}</td>
                  <td className={`${td} li-mono`}>{eur(r.estimated_fee_cents)}</td>
                </tr>
              ))}
              {data.reservations.length === 0 ? (
                <tr>
                  <td className={td} colSpan={8}>
                    None yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <InsightsPanel password={password} />

      <ClubDemand password={password} />

      <section className="mt-10">
        <h2 className="li-display text-lg font-bold tracking-tight text-[#1C2430]">
          Availability requests ({data.requests.length})
        </h2>
        <div className="mt-3 overflow-x-auto rounded-lg border border-[#1C2430]/10">
          <table className="w-full min-w-[720px] border-collapse">
            <thead className="bg-[#E7E2D6]">
              <tr>
                <th className={th}>When</th>
                <th className={th}>Club</th>
                <th className={th}>Date</th>
                <th className={th}>Guest</th>
                <th className={th}>Contact</th>
                <th className={th}>Party</th>
                <th className={th}>Message</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1C2430]/10">
              {data.requests.map((r) => (
                <tr key={r.id}>
                  <td className={td}>{r.created_at}</td>
                  <td className={td}>{r.club_name ?? r.club_slug}</td>
                  <td className={td}>{r.visit_date}</td>
                  <td className={td}>{r.name}</td>
                  <td className={td}>{r.contact}</td>
                  <td className={td}>{r.party_size}</td>
                  <td className={`${td} max-w-[220px]`}>{r.message}</td>
                </tr>
              ))}
              {data.requests.length === 0 ? (
                <tr>
                  <td className={td} colSpan={7}>
                    None yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="li-display text-lg font-bold tracking-tight text-[#1C2430]">
          Partner applications ({data.applications.length})
        </h2>
        <p className="li-mono mt-1 text-[10px] text-[#5B6472]">
          Clubs that asked to be listed. Follow up, then find them below to mark them sponsored once
          they've paid for a featured spot.
        </p>
        <div className="mt-3 overflow-x-auto rounded-lg border border-[#1C2430]/10">
          <table className="w-full min-w-[720px] border-collapse">
            <thead className="bg-[#E7E2D6]">
              <tr>
                <th className={th}>When</th>
                <th className={th}>Club</th>
                <th className={th}>Contact person</th>
                <th className={th}>Contact</th>
                <th className={th}>Country / town</th>
                <th className={th}>Instagram</th>
                <th className={th}>Has system?</th>
                <th className={th}>Message</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1C2430]/10">
              {data.applications.map((a) => (
                <tr key={a.id}>
                  <td className={td}>{a.created_at}</td>
                  <td className={td}>{a.club_name}</td>
                  <td className={td}>{a.contact_name}</td>
                  <td className={td}>{a.contact}</td>
                  <td className={td}>
                    {a.country} {a.town}
                  </td>
                  <td className={td}>{a.instagram}</td>
                  <td className={td}>{a.has_booking_system}</td>
                  <td className={`${td} max-w-[200px]`}>{a.message}</td>
                </tr>
              ))}
              {data.applications.length === 0 ? (
                <tr>
                  <td className={td} colSpan={8}>
                    None yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="li-display text-lg font-bold tracking-tight text-[#1C2430]">
          Reviews ({data.reviews.length})
        </h2>
        <p className="li-mono mt-1 text-[10px] text-[#5B6472]">
          Publish live immediately. Hide anything spammy, fake or off-policy; hidden reviews stop
          showing on the site straight away but stay here so you can re-publish if needed.
        </p>
        <div className="mt-3 overflow-x-auto rounded-lg border border-[#1C2430]/10">
          <table className="w-full min-w-[820px] border-collapse">
            <thead className="bg-[#E7E2D6]">
              <tr>
                <th className={th}>When</th>
                <th className={th}>Club</th>
                <th className={th}>Author</th>
                <th className={th}>Rating</th>
                <th className={th}>Review</th>
                <th className={th}>Visited</th>
                <th className={th}>Booked via</th>
                <th className={th}>Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1C2430]/10">
              {data.reviews.map((r) => (
                <tr key={r.id}>
                  <td className={td}>{r.created_at}</td>
                  <td className={td}>{r.club_name ?? r.club_slug}</td>
                  <td className={td}>{r.author_name}</td>
                  <td className={`${td} li-mono`}>{r.rating} / 5</td>
                  <td className={`${td} max-w-[280px]`}>
                    {r.title ? <p className="font-bold">{r.title}</p> : null}
                    <p>{r.body}</p>
                  </td>
                  <td className={td}>{r.visit_month}</td>
                  <td className={td}>{r.booked_via}</td>
                  <td className={td}>
                    <button
                      disabled={busyReviewId === r.id}
                      onClick={() => toggleReview(r.id, r.status)}
                      className={`li-mono rounded-md border px-2 py-1 text-[10px] transition-colors disabled:opacity-50 ${
                        r.status === "published"
                          ? "border-[#2C4FD6] bg-[#2C4FD6] text-[#F2EFE9]"
                          : "border-[#B03030]/40 text-[#B03030]"
                      }`}
                    >
                      {r.status === "published" ? "Published" : "Hidden, click to publish"}
                    </button>
                  </td>
                </tr>
              ))}
              {data.reviews.length === 0 ? (
                <tr>
                  <td className={td} colSpan={8}>
                    None yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="li-display text-lg font-bold tracking-tight text-[#1C2430]">
          Photos ({data.photos.filter((p) => p.status === "pending").length} pending of{" "}
          {data.photos.length})
        </h2>
        <p className="li-mono mt-1 text-[10px] text-[#5B6472]">
          Nothing here is scraped, every photo was uploaded through the site. Approve sets it as the
          club's live photo; reject deletes the file.
        </p>
        <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.photos.map((p) => (
            <div key={p.id} className="rounded-lg border border-[#1C2430]/10 bg-white/50 p-3">
              <img
                src={`/api/photo/${p.r2_key}`}
                alt={p.club_name ?? p.club_slug}
                className="h-40 w-full rounded-md object-cover"
              />
              <p className="li-display mt-2 text-sm font-bold text-[#1C2430]">
                {p.club_name ?? p.club_slug}
              </p>
              <p className="li-mono text-[10px] text-[#5B6472]">
                {p.uploaded_by_name || "anonymous"} / {p.created_at} / {p.status}
              </p>
              {p.status !== "approved" ? (
                <div className="mt-2 flex gap-2">
                  <button
                    disabled={busyPhotoId === p.id}
                    onClick={() => actOnPhoto(p.id, "approve")}
                    className="li-mono rounded-md border border-[#2C4FD6] bg-[#2C4FD6] px-2 py-1 text-[10px] text-[#F2EFE9] disabled:opacity-50"
                  >
                    Approve
                  </button>
                  <button
                    disabled={busyPhotoId === p.id}
                    onClick={() => actOnPhoto(p.id, "reject")}
                    className="li-mono rounded-md border border-[#B03030]/40 px-2 py-1 text-[10px] text-[#B03030] disabled:opacity-50"
                  >
                    Reject
                  </button>
                </div>
              ) : (
                <p className="li-mono mt-2 text-[10px] text-[#2C4FD6]">Live on club page</p>
              )}
            </div>
          ))}
          {data.photos.length === 0 ? (
            <p className="li-mono text-sm text-[#5B6472]">None yet.</p>
          ) : null}
        </div>
      </section>

      <section className="mt-10 mb-16">
        <h2 className="li-display text-lg font-bold tracking-tight text-[#1C2430]">
          Clubs ({data.clubs.length})
        </h2>
        <p className="li-mono mt-1 text-[10px] text-[#5B6472]">
          Sponsored clubs are pinned to the top of every list on the site. Sell this to owners
          directly, then flip the switch here.
        </p>
        <input
          value={clubQuery}
          onChange={(e) => setClubQuery(e.target.value)}
          placeholder="Search by name or town"
          className="li-display mt-3 h-10 w-full max-w-sm rounded-lg border border-[#1C2430]/15 bg-white/70 px-3 text-sm text-[#1C2430] placeholder:text-[#5B6472] focus:border-[#2C4FD6] focus:outline-none"
        />
        <div className="mt-3 max-h-[420px] overflow-auto rounded-lg border border-[#1C2430]/10">
          <table className="w-full min-w-[560px] border-collapse">
            <thead className="sticky top-0 bg-[#E7E2D6]">
              <tr>
                <th className={th}>Club</th>
                <th className={th}>Location</th>
                <th className={th}>Verified</th>
                <th className={th}>Featured</th>
                <th className={th}>Sponsored</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1C2430]/10">
              {data.clubs
                .filter((c) => {
                  const q = clubQuery.trim().toLowerCase();
                  if (!q) return true;
                  return `${c.name} ${c.town} ${c.region_label}`.toLowerCase().includes(q);
                })
                .map((c) => (
                  <tr key={c.slug}>
                    <td className={td}>{c.name}</td>
                    <td className={td}>
                      {c.town}, {c.region_label}
                    </td>
                    <td className={`${td} li-mono`}>{c.verified ? "yes" : "-"}</td>
                    <td className={`${td} li-mono`}>{c.featured ? "yes" : "-"}</td>
                    <td className={td}>
                      <button
                        disabled={busySlug === c.slug}
                        onClick={() => toggleSponsor(c.slug, Boolean(c.sponsored))}
                        className={`li-mono rounded-md border px-2 py-1 text-[10px] transition-colors disabled:opacity-50 ${
                          c.sponsored
                            ? "border-[#2C4FD6] bg-[#2C4FD6] text-[#F2EFE9]"
                            : "border-[#1C2430]/20 text-[#1C2430] hover:border-[#2C4FD6]"
                        }`}
                      >
                        {c.sponsored ? "Sponsored" : "Mark sponsored"}
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function AdminPage() {
  const [password, setPassword] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const stored = window.sessionStorage.getItem("sunbedly_admin_pw");
    if (stored) setPassword(stored);
    setChecked(true);
  }, []);

  if (!checked) return null;

  return (
    <div className="min-h-dvh bg-[#F2EFE9]">
      {password ? <Dashboard password={password} /> : <LoginGate onSuccess={setPassword} />}
    </div>
  );
}
